import { useQuery } from '@apollo/client';
import { DeviceHub, KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  useAddDevicesToDeviceGroupFromSettings,
  useRemoveDevicesFromDeviceGroup,
} from '~/api/device-groups';
import { ListContainer, ListContent, ListPageContainer } from '~/components/PageLayout';
import { RouterBreadcrumbs } from '~/components/RouterBreadcrumbs';
import { ScrollableRouterTabs } from '~/components/RouterTabs';
import { SelectableList, useSelectableList } from '~/components/SelectableList';
import { SelectableListToolbar } from '~/components/SelectableList/SelectableList';
import { DeviceLogo } from '~/components/devices';
import { AuthorizedLink } from '~/components/links';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import type { DevicesOrderBy } from '~/generated/graphql';
import { useRowSelectionMode } from '~/hooks/data-grid';
import { useTotalCount } from '~/hooks/pagination';
import type { NetworkDeviceGroupDevices__Device as Device } from '../queries/devices.generated';
import {
  NetworkDeviceGroupDevicesDocument,
  NetworkUngroupedDevicesDocument,
} from '../queries/devices.generated';
import { DeviceTable } from './components';
import { useTabs } from './lib/tabs';

const DeviceName = ({ device }: { device: Device }) => {
  return (
    <Box display="flex" alignItems="center">
      <DeviceLogo kind={device.kind} />
      <Typography ml={1}>
        <AuthorizedLink authorized={device.canUpdate.value} entity={device} />
      </Typography>
    </Box>
  );
};

const useAvailableColumns = (): GridColDef<Device>[] => {
  const { t } = useTranslation();
  return [
    {
      field: 'id',
      width: 65,
      valueGetter: (_value, row) => row.id,
    },
    {
      field: 'name',
      headerName: t('common.labels.name'),
      flex: 0.5,
      renderCell: ({ row }) => <DeviceName device={row} />,
    },
    {
      field: 'ipAddress',
      width: 150,
      headerName: t('common.labels.ip_address'),
    },
  ];
};

const useSelectedColumns = (): GridColDef<Device>[] => {
  const { t } = useTranslation();
  return [
    {
      field: 'id',
      width: 65,
      valueGetter: (_value, row) => row.id,
    },
    {
      field: 'name',
      headerName: t('common.labels.name'),
      flex: 0.5,
      renderCell: ({ row }) => <DeviceName device={row} />,
    },
    {
      field: 'deviceGroup',
      headerName: t('common.labels.device_group'),
      flex: 0.5,
      sortable: false,
      valueGetter: (_value, row) => row.groups.map((g) => g.name).join(', '),
    },
    {
      field: 'ipAddress',
      width: 150,
      headerName: t('common.labels.ip_address'),
    },
  ];
};

export const DeviceGroupDevices = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const { deviceGroupId: deviceGroupIdParam } = useParams<{ deviceGroupId: string }>();
  const deviceGroupId = Number(deviceGroupIdParam);

  const tabs = useTabs();

  const availableColumns = useAvailableColumns();
  const selectedColumns = useSelectedColumns();

  const [addToDeviceGroup, { loading: isAdding }] = useAddDevicesToDeviceGroupFromSettings();

  const [removeFromDeviceGroup, { loading: isRemoving }] = useRemoveDevicesFromDeviceGroup();

  const { rowSelectionMode: availableRowSelectionMode } = useRowSelectionMode();

  const {
    search: availableSearch,
    setSearch: setAvailableSearch,
    sortModel: availableSortModel,
    setSortModel: setAvailableSortModel,
    orderBy: availableOrderBy,
    paginationModel: availablePaginationModel,
    setPaginationModel: setAvailablePaginationModel,
    selectedIds: availableSelectedIds,
    handleSelectionChange: handleAvailableSelectionChange,
    clearSelected: clearAvailableSelected,
  } = useSelectableList<DevicesOrderBy>({ initialSort: [{ field: 'name', sort: 'asc' }] });

  const { rowSelectionMode: selectedRowSelectionMode } = useRowSelectionMode();

  const {
    search: selectedSearch,
    setSearch: setSelectedSearch,
    sortModel: selectedSortModel,
    setSortModel: setSelectedSortModel,
    orderBy: selectedOrderBy,
    paginationModel: selectedPaginationModel,
    setPaginationModel: setSelectedPaginationModel,
    selectedIds: selectedIdsInGroup,
    handleSelectionChange: handleSelectedSelectionChange,
    clearSelected: clearSelectedGroup,
  } = useSelectableList<DevicesOrderBy>({ initialSort: [{ field: 'name', sort: 'asc' }] });

  const { data: availableData, loading: availableLoading } = useQuery(
    NetworkUngroupedDevicesDocument,
    {
      variables: {
        networkId: currentNetwork.id,
        search: availableSearch,
        orderBy: availableOrderBy,
        page: availablePaginationModel.page + 1,
        perPage: availablePaginationModel.pageSize,
      },
    },
  );

  const { data: selectedData, loading: selectedLoading } = useQuery(
    NetworkDeviceGroupDevicesDocument,
    {
      variables: {
        networkId: currentNetwork.id,
        deviceGroupId,
        search: selectedSearch,
        orderBy: selectedOrderBy,
        page: selectedPaginationModel.page + 1,
        perPage: selectedPaginationModel.pageSize,
      },
    },
  );

  const availableTotalCount = useTotalCount(availableData?.network?.devices.pageInfo.nodesCount);
  const selectedTotalCount = useTotalCount(
    selectedData?.network?.deviceGroup.devices.pageInfo.nodesCount,
  );

  const handleAddToGroup = async () => {
    await addToDeviceGroup({
      variables: {
        deviceGroupId,
        deviceIds: availableSelectedIds,
      },
    });
    clearAvailableSelected();
    clearSelectedGroup();
  };

  const handleRemoveFromGroup = async () => {
    await removeFromDeviceGroup({
      variables: {
        deviceGroupId,
        deviceIds: selectedIdsInGroup,
      },
    });
    clearAvailableSelected();
    clearSelectedGroup();
  };

  return (
    <>
      <Toolbar
        breadcrumbsLabel={<RouterBreadcrumbs />}
        titleIcon={<DeviceHub />}
        titleText={selectedData?.network?.deviceGroup.name ?? ''}
      />

      <ScrollableRouterTabs current={t('common.labels.devices')} tabs={tabs} />

      <ListPageContainer>
        <ListContainer>
          <ListContent display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
            <SelectableList
              sx={{ flexBasis: '60%', minWidth: 0 }}
              title={t('pages.device_groups.devices.selected_devices.title')}
            >
              <SelectableListToolbar
                actionBarOrientation="default"
                actionIcon={<KeyboardArrowRight />}
                actionLabel={t('pages.device_groups.devices.selected_devices.remove_action')}
                actionLoading={isRemoving}
                onActionClick={handleRemoveFromGroup}
                onSearchChange={setSelectedSearch}
                rowSelectionMode={selectedRowSelectionMode}
                search={selectedSearch}
                searchPlaceholder={t('common.actions.search')}
                selectedIds={selectedIdsInGroup}
                thing="common.pluralize.device_plural"
                totalCount={selectedTotalCount}
              />
              <DeviceTable
                columns={selectedColumns}
                devices={selectedData?.network?.deviceGroup.devices.nodes ?? []}
                emptyHeader={t('pages.device_groups.devices.selected_devices.empty_header')}
                emptyDescription={t(
                  'pages.device_groups.devices.selected_devices.empty_description',
                )}
                loading={selectedLoading}
                onPaginationModelChange={setSelectedPaginationModel}
                onRowSelectionModelChange={handleSelectedSelectionChange(
                  selectedData?.network?.deviceGroup.devices.nodes.map((d) => d.id) ?? [],
                )}
                onSortModelChange={setSelectedSortModel}
                paginationModel={selectedPaginationModel}
                selectedIds={selectedIdsInGroup}
                sortModel={selectedSortModel}
                totalCount={selectedTotalCount}
              />
            </SelectableList>

            <SelectableList
              sx={{ flexBasis: '40%', minWidth: 0 }}
              title={t('pages.device_groups.devices.available_devices.title')}
            >
              <SelectableListToolbar
                actionBarOrientation="inverse"
                actionIcon={<KeyboardArrowLeft />}
                actionLabel={t('pages.device_groups.devices.available_devices.add_action')}
                actionLoading={isAdding}
                onActionClick={handleAddToGroup}
                onSearchChange={setAvailableSearch}
                rowSelectionMode={availableRowSelectionMode}
                search={availableSearch}
                searchPlaceholder={t('common.actions.search')}
                selectedIds={availableSelectedIds}
                thing="common.pluralize.device_plural"
                totalCount={availableTotalCount}
              />
              <DeviceTable
                columns={availableColumns}
                emptyHeader={t('pages.device_groups.devices.available_devices.empty_header')}
                emptyDescription={t(
                  'pages.device_groups.devices.available_devices.empty_description',
                )}
                devices={availableData?.network?.devices.nodes ?? []}
                loading={availableLoading}
                onPaginationModelChange={setAvailablePaginationModel}
                onRowSelectionModelChange={handleAvailableSelectionChange(
                  availableData?.network?.devices.nodes.map((d) => d.id) ?? [],
                )}
                onSortModelChange={setAvailableSortModel}
                paginationModel={availablePaginationModel}
                selectedIds={availableSelectedIds}
                sortModel={availableSortModel}
                totalCount={availableTotalCount}
              />
            </SelectableList>
          </ListContent>
        </ListContainer>
      </ListPageContainer>
    </>
  );
};
